<script lang="ts">
  import type { HTMLInputAttributes } from 'svelte/elements'
  import { Eye, EyeOff, type IconProps } from '@lucide/svelte'
  import i18n from '../../i18n'
  import type { Component } from 'svelte'

  type Props = HTMLInputAttributes & {
    label?: string
    error?: string
    isPassword?: boolean
    variant?: 'default' | 'ghost'
    rightIcon?: Component<IconProps, Record<string, string>, ''>
    sizeVariant?: 'sm' | 'md'
  }

  let {
    autocomplete = 'off',
    label,
    error,
    value = $bindable(),
    variant = 'default',
    rightIcon: RightIcon,
    sizeVariant = 'md',
    ...inputProps
  }: Props = $props()

  let showPassword = $state(false)

  const togglePasswordVisibility = () => {
    showPassword = !showPassword
  }

  let inputType = $derived(
    inputProps.type === 'password'
      ? showPassword
        ? 'text'
        : 'password'
      : inputProps.type || 'text',
  )
</script>

<div class={`input input--${variant} input--${sizeVariant} ${inputProps.disabled ? 'input--disabled' : ''} ${error ? 'input--error' : ''}`}>
  {#if variant === 'default' && label}
    <label for={inputProps.name}>{label}</label>
  {/if}

  <div class="wrapper">
    <input
      {autocomplete}
      bind:value
      {...inputProps}
      aria-label={label}
      type={inputType}
    />
    {#if inputProps.type === 'password'}
      <button
        class="right-icon"
        aria-label={showPassword
          ? $i18n.t('form.hidePassword')
          : $i18n.t('form.showPassword')}
        disabled={inputProps.disabled}
        onclick={togglePasswordVisibility}
        type="button"
      >
        {#if showPassword}
          <Eye size="1rem" />
        {:else}
          <EyeOff size="1rem" />
        {/if}
      </button>
    {:else if RightIcon}
      <div class="right-icon">
        <RightIcon size="1rem" />
      </div>
    {/if}
  </div>

  {#if error}
    <p class="error">{error}</p>
  {/if}
</div>

<style lang="scss">
  .input {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    width: 100%;

    label,
    .error {
      line-height: 150%;
      font-size: 0.75rem;
      color: var(--foreground);
    }

    .error {
      color: var(--red);
      display: none;
    }

    .wrapper {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;

      input {
        height: 2.5rem;
        border-radius: 0.5rem;
        border: none;
        outline: none;
        background: var(--input-background);
        color: var(--input-foreground);
        padding: 0 1rem;
        width: 100%;

        &::placeholder {
          color: var(--input-placeholder);
        }

        &:focus-visible {
          outline: 1px solid var(--stroke);
          outline-offset: 2px;
        }

        &:hover {
          background-color: var(--input-hover-background);
        }

        // Hiding right carets on number inputs - Chrome version
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        // Hiding right carets on number inputs - FF version
        &[type="number"] {
          appearance: textfield;
          -moz-appearance: textfield;
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus,
        &:-webkit-autofill:active {
          -webkit-background-clip: text;
          -webkit-box-shadow: 0 0 0 2.5rem var(--input-background) inset;
          -webkit-text-fill-color: var(--input-foreground);
          transition:
            background-color 5000s ease-in-out 0s,
            color 5000s ease-in-out 0s;
        }

        &:-webkit-autofill:hover {
          -webkit-box-shadow: 0 0 0 2.5rem var(--input-hover-background) inset;
        }
      }

      .right-icon {
        position: absolute;
        height: 2.5rem;
        width: 2.5rem;
        right: 0;
        background: none;
        outline: none;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--input-foreground);
        opacity: 0.7;
        padding: 0;
        border-radius: 0 0.5rem 0.5rem 0;

        &button {
          &:hover,
          &:focus-visible {
            &:not(:disabled) {
              opacity: 1;
            }
          }

          &:focus-visible {
            outline: 1px solid var(--stroke);
            outline-offset: -3px;
          }
        }
      }
    }

    &--error {
      input {
        color: var(--red);
      }

      .error {
        display: block;
      }
    }

    &--disabled {
      opacity: 0.35;
    }

    &--ghost {
      .wrapper {
        input,
        input:hover {
          padding: 0;
          background-color: transparent;
        }

        input:focus-visible {
          outline: none;
        }
      }
    }

    &--sm {
      .wrapper input {
        height: 1.5rem;
        border-radius: 0.5rem;
        padding: 0 0.5rem;
      }
    }
  }
</style>
